import { uniq } from 'lodash';

import type {
  ClaimBarAddedField,
  ClaimBarConfigurationSaveBody,
  ClaimBarFieldEdit,
  ClaimBarFieldEntry,
} from '../types';

/**
 * A scope's unsaved changes, held on the screen until Save sends them in one request. `order` is the whole
 * order once a field was moved, and `null` until then.
 */
export type HeldClaimBar = {
  fields: Record<string, ClaimBarFieldEdit>;
  added: ClaimBarAddedField[];
  order: string[] | null;
};

export const NO_HELD_EDITS: HeldClaimBar = { fields: {}, added: [], order: null };

/**
 * The bar as the screen shows it: the server's entries with the held changes applied, in the held order. A held
 * field the server already carries, saved but not yet cleared, is shown once, from the server.
 */
export const applyHeldEdits = (entries: ClaimBarFieldEntry[], held: HeldClaimBar): ClaimBarFieldEntry[] => {
  const merged: ClaimBarFieldEntry[] = [
    ...entries.map((entry) => ({ ...entry, ...held.fields[entry.field_key] })),
    ...held.added
      .filter((added) => !entries.some((entry) => entry.field_key === added.field_key))
      .map((added) => ({ ...added, ...held.fields[added.field_key] })),
  ];
  if (held.order === null) {
    return merged;
  }
  const position = new Map(held.order.map((key, index) => [key, index]));
  // A key the held order does not name, such as one the server added since, keeps its place after the named ones.
  return merged
    .map((entry, index) => ({ entry, rank: position.get(entry.field_key) ?? held.order!.length + index }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ entry }) => entry);
};

/**
 * The whole order after a drag among the displayed fields. A field the organization does not display keeps its
 * slot, and the displayed ones fill the remaining slots in their new order, so a drag never moves a field the
 * administrator cannot see.
 */
export const orderAfterDrag = (
  allKeys: string[],
  displayedKeys: ReadonlySet<string>,
  reorderedDisplayedKeys: string[]
): string[] => {
  const queue = [...reorderedDisplayedKeys];
  return allKeys.map((key) => (displayedKeys.has(key) ? (queue.shift() as string) : key));
};

/**
 * The held state after a rename or a show/hide. An added field is changed in place, as it has no stored entry. A
 * value set back to what the server stores is no longer held, since saving it would store an override that stops
 * the scope following the Default.
 */
export const withFieldEdit = (
  held: HeldClaimBar,
  fieldKey: string,
  typed: ClaimBarFieldEdit,
  stored: ClaimBarFieldEntry | undefined
): HeldClaimBar => {
  // The server stores a name trimmed, so it is held trimmed too: the save body, the held state and the stored value
  // then agree, which reading a save back depends on.
  const edit = typed.display_name === undefined ? typed : { ...typed, display_name: typed.display_name.trim() };
  if (held.added.some((added) => added.field_key === fieldKey)) {
    return {
      ...held,
      added: held.added.map((added) => (added.field_key === fieldKey ? { ...added, ...edit } : added)),
    };
  }
  const changed = Object.fromEntries(
    Object.entries({ ...held.fields[fieldKey], ...edit }).filter(
      ([name, value]) => stored?.[name as keyof ClaimBarFieldEdit] !== value
    )
  ) as ClaimBarFieldEdit;
  const { [fieldKey]: _previous, ...others } = held.fields;
  return { ...held, fields: Object.keys(changed).length > 0 ? { ...others, [fieldKey]: changed } : others };
};

/** The held state after a drag. An order the bar takes without one is no longer held, for the same reason. */
export const withOrder = (held: HeldClaimBar, entries: ClaimBarFieldEntry[], order: string[]): HeldClaimBar => {
  const unordered = applyHeldEdits(entries, { ...held, order: null }).map((entry) => entry.field_key);
  const isUnchanged = unordered.length === order.length && unordered.every((key, index) => key === order[index]);
  return { ...held, order: isUnchanged ? null : order };
};

/**
 * Whether stored entries already hold every held change: each edited and added field with the values held, and
 * the held order among the fields it names. A save is stored whole or not at all, so this tells the two apart.
 */
export const isHeldStored = (held: HeldClaimBar, stored: ClaimBarFieldEntry[]): boolean => {
  const byKey = new Map(stored.map((entry) => [entry.field_key, entry]));
  const holds = (fieldKey: string, values: ClaimBarFieldEdit) => {
    const entry = byKey.get(fieldKey);
    return (
      entry !== undefined &&
      Object.entries(values).every(([name, value]) => entry[name as keyof ClaimBarFieldEdit] === value)
    );
  };
  const named = new Set(held.order ?? []);
  const storedOrder = stored.map((entry) => entry.field_key).filter((key) => named.has(key));
  return (
    Object.entries(held.fields).every(([fieldKey, edit]) => holds(fieldKey, edit)) &&
    held.added.every(({ field_key, ...values }) => holds(field_key, values)) &&
    (held.order === null ||
      (storedOrder.length === held.order.length && storedOrder.every((key, index) => key === held.order![index])))
  );
};

/**
 * The fields whose held name is blank: a field needs a name, so nothing is saved while one is. Each is named as it is
 * stored; a field this Save adds by its name in the scope's catalog, or by its key where the pane read none, since its
 * card shows the blank name.
 */
export const blankNamedFields = (
  held: HeldClaimBar,
  entries: ClaimBarFieldEntry[],
  catalogFields: { field_key: string; label: string }[] = []
): { fieldKey: string; label: string }[] =>
  uniq([
    ...Object.entries(held.fields)
      .filter(([, edit]) => edit.display_name === '')
      .map(([fieldKey]) => fieldKey),
    ...held.added.filter((added) => added.display_name === '').map((added) => added.field_key),
  ]).map((fieldKey) => ({
    fieldKey,
    label:
      entries.find((entry) => entry.field_key === fieldKey)?.display_name ||
      catalogFields.find((field) => field.field_key === fieldKey)?.label ||
      fieldKey,
  }));

export const buildSaveBody = (
  scope: { lob: string | null; subOrganizationId: number | null },
  held: HeldClaimBar
): ClaimBarConfigurationSaveBody => ({
  lob: scope.lob,
  sub_organization_id: scope.subOrganizationId,
  fields: Object.entries(held.fields).map(([fieldKey, edit]) => ({ field_key: fieldKey, ...edit })),
  added: held.added,
  ...(held.order === null ? {} : { order: held.order }),
});
